import React from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaDna, FaFacebook, FaTwitter, FaLinkedin, FaGithub, FaHome, FaUser, FaUserPlus, FaInfoCircle, FaServer, FaShieldAlt, FaFileContract } from 'react-icons/fa';

const Footer = () => {
  const styles = {
    footer: {
      backgroundColor: '#1a365d',
      color: '#ffffff',
      padding: '4rem 0 2rem',
      marginTop: '4rem',
      textAlign: 'left',
    },
    container: {
      maxWidth: '1200px',
    },
    logo: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      marginBottom: '1rem',
    },
    logoIcon: {
      fontSize: '2rem',
      color: '#ffffff',
    },
    logoText: {
      fontSize: '1.5rem',
      fontWeight: '700',
      margin: '0',
      color: '#ffffff',
    },
    description: {
      color: 'rgba(255,255,255,0.8)',
      fontSize: '0.95rem',
      lineHeight: '1.6',
      marginBottom: '1.5rem',
    },
    socialLinks: {
      display: 'flex',
      gap: '1rem',
      marginBottom: '2rem',
    },
    socialIcon: {
      color: 'rgba(255,255,255,0.8)',
      fontSize: '1.5rem',
      transition: 'all 0.2s ease',
      '&:hover': {
        color: '#ffffff',
        transform: 'translateY(-2px)',
      },
    },
    sectionTitle: {
      fontSize: '1.1rem',
      fontWeight: '600',
      marginBottom: '1.25rem',
      color: '#ffffff',
    },
    linksList: {
      listStyle: 'none',
      padding: '0',
      margin: '0',
    },
    linkItem: {
      marginBottom: '0.75rem',
    },
    link: {
      color: 'rgba(255,255,255,0.8)',
      textDecoration: 'none',
      fontSize: '0.95rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      transition: 'all 0.2s ease',
      '&:hover': {
        color: '#ffffff',
        transform: 'translateX(5px)',
      },
    },
    divider: {
      borderTop: '1px solid rgba(255,255,255,0.1)',
      margin: '2rem 0',
    },
    copyright: {
      color: 'rgba(255,255,255,0.6)',
      fontSize: '0.9rem',
      textAlign: 'center',
    },
    icon: {
      fontSize: '1rem',
    },
  };

  return (
    <footer style={styles.footer}>
      <Container style={styles.container}>
        <Row className="mb-4">
          <Col lg={4}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div style={styles.logo}>
                <FaDna style={styles.logoIcon} />
                <h2 style={styles.logoText}>DNACloudHub</h2>
              </div>
              <p style={styles.description}>
                Empowering researchers with cloud-based tools for genetic analysis.
                Run advanced qpAdm models and manage your research efficiently in one place.
              </p>
              <div style={styles.socialLinks}>
                <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" style={styles.socialIcon}>
                  <FaFacebook />
                </a>
                <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" style={styles.socialIcon}>
                  <FaTwitter />
                </a>
                <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" style={styles.socialIcon}>
                  <FaLinkedin />
                </a>
                <a href="https://github.com" target="_blank" rel="noopener noreferrer" style={styles.socialIcon}>
                  <FaGithub />
                </a>
              </div>
            </motion.div>
          </Col>

          <Col lg={4}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <h3 style={styles.sectionTitle}>Quick Links</h3>
              <ul style={styles.linksList}>
                <li style={styles.linkItem}>
                  <Link to="/" style={styles.link}>
                    <FaHome style={styles.icon} /> Home
                  </Link>
                </li>
                <li style={styles.linkItem}>
                  <Link to="/login" style={styles.link}>
                    <FaUser style={styles.icon} /> Login
                  </Link>
                </li>
                <li style={styles.linkItem}>
                  <Link to="/signup" style={styles.link}>
                    <FaUserPlus style={styles.icon} /> Sign Up
                  </Link>
                </li>
                <li style={styles.linkItem}>
                  <Link to="/about" style={styles.link}>
                    <FaInfoCircle style={styles.icon} /> About
                  </Link>
                </li>
                <li style={styles.linkItem}>
                  <Link to="/run_qpadm" style={styles.link}>
                    <FaServer style={styles.icon} /> Run qpAdm
                  </Link>
                </li>
              </ul>
            </motion.div>
          </Col>

          <Col lg={4}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <h3 style={styles.sectionTitle}>Legal</h3>
              <ul style={styles.linksList}>
                <li style={styles.linkItem}>
                  <Link to="/privacy_policy" style={styles.link}>
                    <FaShieldAlt style={styles.icon} /> Privacy Policy
                  </Link>
                </li>
                <li style={styles.linkItem}>
                  <Link to="/terms_and_conditions" style={styles.link}>
                    <FaFileContract style={styles.icon} /> Terms and Conditions
                  </Link>
                </li>
              </ul>
            </motion.div>
          </Col>
        </Row>

        <div style={styles.divider} />

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          <p style={styles.copyright}>
            &copy; {new Date().getFullYear()} DNACloudHub. All rights reserved.
          </p>
        </motion.div>
      </Container>
    </footer>
  );
};

export default Footer;
